/**
 * iOS standalone PWA: after returning from an external payment page the document
 * often full-reloads with a permanently shrunk innerHeight/100svh (~Safari chrome).
 * In-memory baseline is lost on reload, so we persist a healthy baseline in sessionStorage
 * and also fall back to a screen-chrome heuristic.
 *
 * Must not run (or must not use screen-chrome restore) inside Telegram Mini App /
 * ordinary browser tabs: there screen.height is the monitor and innerHeight is a
 * small iframe/window — the heuristic falsely “restores” almost full screen height.
 */
const STORAGE_KEY = 'pwaViewportBaseline'
const HEALTHY_CHROME_MAX = 80
const COLLAPSE_EXTRA_PX = 60

let baselineInnerHeight = 0
let baselineChromeGap = 0
let primed = false

function isTextInputFocused() {
  const tag = document.activeElement?.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA'
}

/** Real installed PWA chrome — not Telegram / not a normal browser tab. */
function isInstalledPwaDisplayMode() {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: minimal-ui)').matches
    || window.navigator.standalone === true
  )
}

function measure() {
  const inner = window.innerHeight || 0
  const screenH = window.screen?.height || 0

  return {
    inner,
    screenH,
    chromeGap: screenH > 0 ? screenH - inner : 0,
  }
}

function readStoredBaseline() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }

    const data = JSON.parse(raw)
    if (!data?.inner || !data?.screenH) {
      return null
    }

    // Ignore baseline from another orientation / device size
    if (Math.abs(data.screenH - (window.screen?.height || 0)) > 2) {
      return null
    }

    return data
  } catch {
    return null
  }
}

function writeStoredBaseline(inner, chromeGap, screenH) {
  try {
    // Переживает reload после внешней оплаты: iOS PWA иначе стартует уже со сжатым innerHeight
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        inner,
        chromeGap,
        screenH,
        savedAt: Date.now(),
      }),
    )
  } catch {
    // ignore quota / private mode
  }
}

function adoptBaseline(inner, chromeGap) {
  baselineInnerHeight = inner
  baselineChromeGap = chromeGap
  primed = true
}

function restoreBaselineFromStorage() {
  const stored = readStoredBaseline()
  if (!stored) {
    return false
  }

  adoptBaseline(stored.inner, stored.chromeGap ?? 0)
  return true
}

function isHealthyViewport(m) {
  return m.inner > 0 && m.chromeGap >= 0 && m.chromeGap <= HEALTHY_CHROME_MAX
}

function isCollapsedViewport(m) {
  if (!m.inner || !m.screenH) {
    return false
  }

  // screen − inner is meaningless outside an installed PWA (TMA iframe, desktop browser, …)
  if (!isInstalledPwaDisplayMode()) {
    return false
  }

  // Primary: vs remembered healthy baseline
  if (primed && baselineInnerHeight - m.inner > COLLAPSE_EXTRA_PX && m.chromeGap > baselineChromeGap + COLLAPSE_EXTRA_PX) {
    return true
  }

  // Fallback after cold reload with no / bad baseline: gap looks like Safari chrome reserved
  if (m.chromeGap > HEALTHY_CHROME_MAX + COLLAPSE_EXTRA_PX) {
    return true
  }

  return false
}

function resolveRestoredHeight(m) {
  if (primed && baselineInnerHeight > m.inner) {
    return baselineInnerHeight
  }

  const stored = readStoredBaseline()
  if (stored?.inner && stored.inner > m.inner) {
    adoptBaseline(stored.inner, stored.chromeGap ?? baselineChromeGap)
    return stored.inner
  }

  // Last resort: screen minus a normal standalone chrome (~status bar)
  const fallbackChrome = Math.min(baselineChromeGap || 47, HEALTHY_CHROME_MAX)
  return Math.max(m.inner, m.screenH - fallbackChrome)
}

function primeBaseline(m = measure()) {
  if (!m.inner) {
    return
  }

  // Prefer persisted healthy baseline on startup
  if (!primed) {
    restoreBaselineFromStorage()
  }

  // Only promote baseline while viewport looks healthy (never learn from collapsed state)
  if (isHealthyViewport(m) && (!primed || m.inner >= baselineInnerHeight)) {
    adoptBaseline(m.inner, m.chromeGap)
    writeStoredBaseline(m.inner, m.chromeGap, m.screenH)
  }
}

function applyVisualViewportHeight(root) {
  const vv = window.visualViewport
  if (!vv) {
    root.style.setProperty('--document-height', `${window.innerHeight || 0}px`)
    root.style.setProperty('--document-offset-top', '0px')
    root.style.setProperty('--document-offset-bottom', '0px')
    return
  }

  // Soft keyboard (and browser chrome) shrink visualViewport; keep layout in the visible area
  root.style.setProperty('--document-height', `${Math.round(vv.height)}px`)
  root.style.setProperty('--document-offset-top', `${Math.round(vv.offsetTop)}px`)
  root.style.setProperty('--document-offset-bottom', '0px')
}

export function syncDocumentHeight() {
  const root = document.documentElement
  const m = measure()

  if (!m.inner) {
    return
  }

  // Defense in depth: never apply PWA screen-restore outside installed PWA
  if (!isInstalledPwaDisplayMode()) {
    root.style.setProperty('--document-height', `${Math.round(m.inner)}px`)
    root.style.setProperty('--document-offset-top', '0px')
    root.style.setProperty('--document-offset-bottom', '0px')
    return
  }

  if (isTextInputFocused()) {
    // Do not restore payment-collapse baseline while typing — that would keep full
    // innerHeight under the keyboard and hide bottom inputs (e.g. chat).
    applyVisualViewportHeight(root)
    window.scrollTo(0, 0)
    return
  }

  primeBaseline(m)

  const collapsed = isCollapsedViewport(m)
  const height = collapsed ? resolveRestoredHeight(m) : (primed ? Math.max(m.inner, baselineInnerHeight) : m.inner)

  root.style.setProperty('--document-height', `${Math.round(height)}px`)
  root.style.setProperty('--document-offset-top', '0px')
  root.style.setProperty('--document-offset-bottom', '0px')

  if (collapsed) {
    void root.offsetHeight
    window.scrollTo(0, 0)
  }
}

function syncDocumentHeightDeferred() {
  syncDocumentHeight()
  requestAnimationFrame(syncDocumentHeight)
  setTimeout(syncDocumentHeight, 50)
  setTimeout(syncDocumentHeight, 300)
  setTimeout(syncDocumentHeight, 1000)
}

/**
 * Call once before leaving to an external URL (payment) so baseline survives reload.
 */
export function persistDocumentHeightBaseline() {
  const m = measure()
  if (isHealthyViewport(m)) {
    adoptBaseline(m.inner, m.chromeGap)
    writeStoredBaseline(m.inner, m.chromeGap, m.screenH)
  } else if (primed && baselineInnerHeight > 0) {
    writeStoredBaseline(baselineInnerHeight, baselineChromeGap, m.screenH || window.screen?.height || 0)
  }
}

/**
 * @returns {() => void} unsubscribe
 */
export function startDocumentHeightSync() {
  restoreBaselineFromStorage()
  syncDocumentHeightDeferred()

  const onResize = () => {
    syncDocumentHeight()
  }

  const onFocusChange = () => {
    syncDocumentHeight()
    requestAnimationFrame(syncDocumentHeight)
    setTimeout(syncDocumentHeight, 50)
    setTimeout(syncDocumentHeight, 300)
  }

  const onResume = () => {
    syncDocumentHeightDeferred()
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      onResume()
    } else {
      // Leaving the app (e.g. to payment) — persist while we still can
      persistDocumentHeightBaseline()
    }
  }

  const onPageHide = () => {
    persistDocumentHeightBaseline()
  }

  window.addEventListener('resize', onResize)
  window.visualViewport?.addEventListener('resize', onResize)
  // iOS shifts offsetTop when focusing inputs without always firing resize
  window.visualViewport?.addEventListener('scroll', onResize)
  window.addEventListener('orientationchange', onResume)
  window.addEventListener('pageshow', onResume)
  window.addEventListener('focus', onResume)
  window.addEventListener('pagehide', onPageHide)
  document.addEventListener('visibilitychange', onVisibilityChange)
  document.addEventListener('focusin', onFocusChange)
  document.addEventListener('focusout', onFocusChange)

  return () => {
    window.removeEventListener('resize', onResize)
    window.visualViewport?.removeEventListener('resize', onResize)
    window.visualViewport?.removeEventListener('scroll', onResize)
    window.removeEventListener('orientationchange', onResume)
    window.removeEventListener('pageshow', onResume)
    window.removeEventListener('focus', onResume)
    window.removeEventListener('pagehide', onPageHide)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    document.removeEventListener('focusin', onFocusChange)
    document.removeEventListener('focusout', onFocusChange)
  }
}

/** Extra fields for the on-screen debug overlay */
export function getDocumentHeightDebugInfo() {
  const m = measure()
  const focused = isTextInputFocused()
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  const installedPwa = isInstalledPwaDisplayMode()
  const collapsed = installedPwa && !focused && isCollapsedViewport(m)
  const stored = readStoredBaseline()

  let appliedHeight
  if (!installedPwa) {
    appliedHeight = Math.round(m.inner)
  } else if (focused && vv) {
    appliedHeight = Math.round(vv.height)
  } else if (collapsed) {
    appliedHeight = resolveRestoredHeight(m)
  } else {
    appliedHeight = primed ? Math.max(m.inner, baselineInnerHeight) : m.inner
  }

  return {
    primed,
    baselineInnerHeight,
    baselineChromeGap,
    collapsed,
    inputFocused: focused,
    installedPwa,
    storedInner: stored?.inner ?? 0,
    storedChrome: stored?.chromeGap ?? 0,
    appliedHeight,
  }
}
